import React, { useState } from 'react';
import { Coins, AlertCircle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useCredits, CreditCheckResult, CreditOperation } from '@/hooks/useCredits';
import { CreditPurchaseModal } from './CreditPurchaseModal';

interface CreditConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  operationType: CreditOperation;
  operationName: string;
  onConfirm: () => void;
  onCancel?: () => void;
}

// Friendly names for operation types
const OPERATION_NAMES: Record<string, string> = {
  shotstack_render: 'Video Render',
  shotstack_preview: 'Video Preview',
  image_generation: 'Image Generation',
  video_generation: 'Video Generation',
  music_generation: 'Music Generation',
  background_removal: 'Background Removal',
  composite_background: 'Background Composite',
  script_analysis: 'Script Analysis',
};

export const CreditConfirmDialog: React.FC<CreditConfirmDialogProps> = ({
  open,
  onOpenChange,
  operationType,
  operationName,
  onConfirm,
  onCancel,
}) => {
  const { balance, checkCredits, loading } = useCredits();
  const [creditCheck, setCreditCheck] = useState<CreditCheckResult | null>(null);
  const [checking, setChecking] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  // Check credits when dialog opens
  React.useEffect(() => {
    if (open) {
      setChecking(true);
      checkCredits(operationType).then((result) => {
        setCreditCheck(result);
        setChecking(false);
      });
    }
  }, [open, operationType, checkCredits]);

  const handleConfirm = () => {
    onOpenChange(false);
    onConfirm();
  };

  const handleCancel = () => {
    onOpenChange(false);
    onCancel?.();
  };

  const handleBuyCredits = () => {
    setShowPurchaseModal(true);
  };

  // If it's a free operation, just confirm immediately
  if (creditCheck?.isFree) {
    React.useEffect(() => {
      if (open && creditCheck?.isFree) {
        handleConfirm();
      }
    }, [open, creditCheck?.isFree]);
    return null;
  }

  const displayName = operationName || OPERATION_NAMES[operationType] || operationType;
  const hasEnoughCredits = creditCheck?.hasCredits ?? false;
  const cost = creditCheck?.cost ?? 1;
  const totalCredits = creditCheck?.totalCredits ?? balance?.totalCredits ?? 0;

  return (
    <>
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent
          style={{
            background: 'var(--hj-surface-1)',
            border: '1px solid var(--hj-hairline)',
            color: 'var(--hj-ink)',
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle asChild>
              <h2
                className="font-hj-display flex items-center gap-2 m-0"
                style={{
                  fontSize: 22,
                  color: 'var(--hj-ink)',
                  letterSpacing: '-0.02em',
                  fontWeight: 700,
                }}
              >
                {hasEnoughCredits ? (
                  <Coins className="h-5 w-5" style={{ color: 'var(--hj-yellow-ink)' }} />
                ) : (
                  <AlertCircle className="h-5 w-5" style={{ color: 'hsl(var(--destructive))' }} />
                )}
                {hasEnoughCredits ? 'Confirm video token usage' : 'Not enough video tokens'}
              </h2>
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="flex flex-col gap-3" style={{ marginTop: 4 }}>
                {checking ? (
                  <p style={{ fontSize: 13.5, color: 'var(--hj-ink-3)' }}>Checking video tokens…</p>
                ) : hasEnoughCredits ? (
                  <>
                    <p className="m-0" style={{ fontSize: 13.5, color: 'var(--hj-ink-2)', lineHeight: 1.5 }}>
                      <strong style={{ color: 'var(--hj-ink)' }}>{displayName}</strong> will use{' '}
                      <strong style={{ color: 'var(--hj-ink)' }}>
                        {cost} video token{cost !== 1 ? 's' : ''}
                      </strong>
                      .
                    </p>
                    <div
                      style={{
                        padding: 12,
                        borderRadius: 'var(--hj-r-sm)',
                        background: 'var(--hj-surface-2)',
                        border: '1px solid var(--hj-hairline-soft)',
                        fontSize: 13,
                      }}
                    >
                      <div className="flex justify-between">
                        <span style={{ color: 'var(--hj-ink-3)' }}>Current balance</span>
                        <span
                          className="tabular"
                          style={{ color: 'var(--hj-ink)', fontFamily: 'var(--hj-font-mono)', fontWeight: 600 }}
                        >
                          {totalCredits}
                        </span>
                      </div>
                      <div className="flex justify-between" style={{ marginTop: 4 }}>
                        <span style={{ color: 'var(--hj-ink-3)' }}>After this operation</span>
                        <span
                          className="tabular"
                          style={{ color: 'var(--hj-ink-2)', fontFamily: 'var(--hj-font-mono)' }}
                        >
                          {totalCredits - cost}
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="m-0" style={{ fontSize: 13.5, color: 'var(--hj-ink-2)', lineHeight: 1.5 }}>
                      You need{' '}
                      <strong style={{ color: 'var(--hj-ink)' }}>
                        {cost} video token{cost !== 1 ? 's' : ''}
                      </strong>{' '}
                      for <strong style={{ color: 'var(--hj-ink)' }}>{displayName}</strong> but you only have{' '}
                      <strong style={{ color: 'var(--hj-ink)' }}>{totalCredits}</strong>.
                    </p>
                    <p className="m-0" style={{ fontSize: 12.5, color: 'var(--hj-ink-3)' }}>
                      Top up to keep using the AI features.
                    </p>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={handleCancel}
              asChild
            >
              <button
                type="button"
                style={{
                  padding: '8px 16px',
                  borderRadius: 999,
                  background: 'transparent',
                  border: '1px solid var(--hj-hairline)',
                  color: 'var(--hj-ink-2)',
                  fontSize: 13,
                  fontWeight: 500,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  minHeight: 40,
                }}
              >
                Cancel
              </button>
            </AlertDialogCancel>
            {hasEnoughCredits ? (
              <AlertDialogAction onClick={handleConfirm} disabled={checking} asChild>
                <button
                  type="button"
                  className="inline-flex items-center justify-center gap-1.5"
                  style={{
                    padding: '8px 18px',
                    borderRadius: 999,
                    background: 'var(--hj-blue)',
                    border: '1px solid var(--hj-blue)',
                    color: 'oklch(0.18 0.005 70)',
                    fontSize: 13,
                    fontWeight: 700,
                    fontFamily: 'inherit',
                    cursor: checking ? 'not-allowed' : 'pointer',
                    opacity: checking ? 0.6 : 1,
                    minHeight: 40,
                  }}
                >
                  Continue ({cost} video token{cost !== 1 ? 's' : ''})
                </button>
              </AlertDialogAction>
            ) : (
              <button
                type="button"
                onClick={handleBuyCredits}
                className="inline-flex items-center gap-1.5"
                style={{
                  padding: '8px 18px',
                  borderRadius: 999,
                  background: 'var(--hj-blue)',
                  border: '1px solid var(--hj-blue)',
                  color: 'oklch(0.18 0.005 70)',
                  fontSize: 13,
                  fontWeight: 700,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  minHeight: 40,
                }}
              >
                <Coins className="h-4 w-4" />
                Buy video tokens
              </button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CreditPurchaseModal
        open={showPurchaseModal}
        onOpenChange={setShowPurchaseModal}
      />
    </>
  );
};

// Hook for easy credit confirmation before operations
export function useCreditConfirm() {
  const [dialogState, setDialogState] = useState<{
    open: boolean;
    operationType: CreditOperation;
    operationName: string;
    onConfirm: () => void;
    onCancel?: () => void;
  }>({
    open: false,
    operationType: 'shotstack_render',
    operationName: '',
    onConfirm: () => {},
  });

  const confirmCredit = (
    operationType: CreditOperation,
    operationName: string,
    onConfirm: () => void,
    onCancel?: () => void
  ) => {
    setDialogState({
      open: true,
      operationType,
      operationName,
      onConfirm,
      onCancel,
    });
  };

  const CreditConfirmDialogComponent = () => (
    <CreditConfirmDialog
      open={dialogState.open}
      onOpenChange={(open) => setDialogState((s) => ({ ...s, open }))}
      operationType={dialogState.operationType}
      operationName={dialogState.operationName}
      onConfirm={dialogState.onConfirm}
      onCancel={dialogState.onCancel}
    />
  );

  return {
    confirmCredit,
    CreditConfirmDialog: CreditConfirmDialogComponent,
  };
}
